'use client';

import { ADVISORS_MATCH_SKU, buildAdvisorMatchUrl } from '@ria-client/advisor-search/constants';
import {
  AnalyticsSkuViewed,
  useAnalyticsSkuViewedContext,
} from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import { useAnonymousId } from '@ria-client/segment/components/AnonymousIdProvider';
import { ChevronRight } from 'lucide-react';
import { forwardRef } from 'react';

export type MatchCtaLinkProps = {
  label: string;
  actionSection: string;
  crd?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  withShadow?: boolean;
  showChevron?: boolean;
  uppercase?: boolean;
  hideAdBadge?: boolean;
  inverseDisclaimer?: boolean;
  className?: string;
};

const SIZE_STYLES = {
  sm: 'rounded-[2px] px-3 py-1.5 text-[0.8125rem]',
  md: 'rounded-[2px] px-2.5 py-1.5 text-sm',
  lg: 'rounded-[3px] px-5 py-2 text-base',
} as const;

export function MatchCtaLink(props: MatchCtaLinkProps) {
  const { actionSection } = props;

  return (
    <AnalyticsSkuViewed
      impressionSection={actionSection}
      impressionPosition={0}
      monetizable={ADVISORS_MATCH_SKU.monetizable}
      source={ADVISORS_MATCH_SKU.skuSource}
      name={ADVISORS_MATCH_SKU.skuName}
      productType={ADVISORS_MATCH_SKU.skuProductType}
      level={ADVISORS_MATCH_SKU.skuLevel}
      productId={ADVISORS_MATCH_SKU.productId}
      productName={ADVISORS_MATCH_SKU.productName}
    >
      <MatchCtaLinkInner {...props} />
    </AnalyticsSkuViewed>
  );
}

const MatchCtaLinkInner = forwardRef<HTMLDivElement, MatchCtaLinkProps>(function MatchCtaLinkInner(
  {
    label,
    actionSection,
    crd,
    size = 'md',
    fullWidth = false,
    withShadow = false,
    showChevron = false,
    uppercase = false,
    hideAdBadge = false,
    inverseDisclaimer = false,
    className,
  },
  ref,
) {
  const { fireShoppingFinishedEvent } = useAnalyticsSkuViewedContext();
  const anonymousId = useAnonymousId();
  const href = buildAdvisorMatchUrl({
    utmSource: 'nerdwallet',
    utmMedium: 'find_advisor',
    utmCampaign: actionSection,
    utmContent: crd,
    anonymousId: anonymousId ?? undefined,
  });

  const handleClick = () => {
    void fireShoppingFinishedEvent({
      destinationUrl: href,
      ctaType: 'advisor_match_cta',
      finishType: 'external_application',
      additionalAttributes: {
        action_section: actionSection,
        ...(crd ? { crd } : {}),
      },
    });
  };

  return (
    <div ref={ref} className="flex shrink-0 flex-col gap-2">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className={`items-center justify-center gap-2 bg-secondary-foreground font-bold text-primary-foreground no-underline hover:bg-[var(--content-blue-dark)] ${
          fullWidth ? 'flex w-full' : 'inline-flex w-auto'
        } ${SIZE_STYLES[size]} ${className ?? ''} ${uppercase ? 'tracking-[0.5px] uppercase' : ''} ${
          withShadow
            ? 'shadow-[0_4px_20px_color-mix(in_srgb,var(--secondary-foreground)_20%,transparent)]'
            : ''
        }`}
      >
        {!hideAdBadge && <AdBadge size={size === 'lg' ? 'md' : 'sm'} />}
        {label}
        {showChevron && <ChevronRight aria-hidden="true" className="size-[18px]" />}
      </a>
      <p
        className={`typography-text-small text-center ${
          inverseDisclaimer ? 'text-primary-foreground/80' : 'text-black/55'
        }`}
      >
        on NerdWallet Advisors Match
      </p>
    </div>
  );
});

type AdBadgeProps = { size: 'sm' | 'md' };

function AdBadge({ size }: AdBadgeProps) {
  return (
    <span
      aria-label="Advertisement"
      className={`rounded-[2px] bg-white/18 px-1.5 py-px font-bold tracking-[0.3px] ${
        size === 'sm' ? 'text-[0.5625rem]' : 'text-[0.625rem]'
      }`}
    >
      AD
    </span>
  );
}
